/**
 * Kolumna „Akceptacja" na kartotekach Handlu — stan obiegu przy dokumencie żyjącym w Symfonii.
 *
 * Dane idą WYŁĄCZNIE z naszej bazy, osobnym zapytaniem od listy dokumentów. Lista czeka na
 * Symfonię; gdyby status jechał tą samą drogą, kolumna dziedziczyłaby jej opóźnienia, a przy
 * niedostępnym ERP-ie znikałaby razem z całym ekranem — mimo że obieg jest naszą sprawą.
 *
 * Brak wniosku to normalny stan, nie brak danych: pokazujemy kreskę, nie „ładowanie".
 *
 * Etykiety biorą TE SAME klucze co baner na formatce (`tenant.processes.banner.status.*`) —
 * dwa słowniki oznaczałyby, że lista mówi co innego niż dokument, w który się właśnie wchodzi.
 * Zakończona sprawa pokazuje NAZWĘ etapu końcowego (jak baner) w kolorze neutralnym — nazwę
 * nadaje autor procesu, więc kolor „sukces/porażka" mógłby ją kłamliwie opisać.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import type { ColumnDefinition } from '@/components/configurable-table';
import {
  fetchApprovalStatuses,
  type HmApprovalStatus,
  type HmLinkKind,
} from '@/lib/handel-magazyn/document-links-api';
import type { InstanceStatus } from '@/lib/processes/approvals-api';
import { FINAL_NODE_CLASS, finalNodeLabelOrNull } from '@/lib/processes/step-tone';

/**
 * Kolorystyka per status — czytelna na pierwszy rzut oka, bez czytania tekstu. Status bez wpisu
 * (np. `cancelled`) dostaje kolor neutralny.
 */
const STATUS_CLASS: Partial<Record<InstanceStatus, string>> = {
  active: 'bg-amber-100 text-amber-800',
  suspended: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
};

export function approvalStatusesKey(
  kind: HmLinkKind,
  companyId: string | null,
): [string, string, HmLinkKind, string | null] {
  return ['handel-magazyn', 'approval-statuses', kind, companyId];
}

/**
 * Zwraca definicję kolumny albo `null`, gdy nie ma czego pokazać (brak firmy, brak modułu
 * „Procesy", brak połączenia z Symfonią). Wołający dokłada ją do listy kolumn przez `...(col ? [col] : [])`.
 */
export function useApprovalColumn<T extends { id: number }>(
  kind: HmLinkKind,
  companyId: string | null,
): ColumnDefinition<T> | null {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: approvalStatusesKey(kind, companyId),
    queryFn: () => fetchApprovalStatuses({ kind, companyId: companyId! }),
    enabled: !!companyId,
    // Odmowa (brak modułu / brak integracji) nie jest stanem do ponawiania.
    retry: false,
  });

  if (!companyId || query.isError) return null;

  const statuses = query.data ?? new Map<string, HmApprovalStatus>();
  // Zakończona sprawa → nazwa etapu końcowego; w toku / anulowana / starsza (albo nazwa pusta) → status.
  const labelFor = (hit: HmApprovalStatus): string =>
    finalNodeLabelOrNull(hit.finalNodeLabel) ??
    t(`tenant.processes.banner.status.${hit.status}`, hit.status);

  return {
    id: 'approvalStatus',
    header: t('tenant.handelMagazyn.columns.approval'),
    // Wartość tekstowa dla sortowania, filtra i eksportu — pusta, gdy dokument nie ma obiegu.
    accessor: (row) => {
      const hit = statuses.get(String(row.id));
      return hit ? labelFor(hit) : '';
    },
    defaultWidth: 130,
    renderCell: (_v: unknown, row: T): ReactElement => {
      const hit = statuses.get(String(row.id));
      if (!hit) return <span className="text-text-tertiary">—</span>;
      const colorClass = finalNodeLabelOrNull(hit.finalNodeLabel)
        ? FINAL_NODE_CLASS
        : (STATUS_CLASS[hit.status] ?? 'bg-bg-tertiary text-text-secondary');
      return (
        <span
          data-testid={`hm-approval-status-${row.id}`}
          data-status={hit.status}
          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${colorClass}`}
        >
          {labelFor(hit)}
        </span>
      );
    },
  };
}
